import { ElementRef, signal, WritableSignal } from '@angular/core';

export interface KeyboardNavStore {
  readonly highlightedIndex: WritableSignal<number>;
  onKeydown(
    event: KeyboardEvent,
    optionItems: readonly ElementRef<HTMLElement>[]
  ): void;
  reset(): void;
}

/** Keyboard navigation (ArrowDown/Up/Enter/Escape) for dropdown option lists. */
export function createKeyboardNav<T>(options: {
  results: () => readonly T[];
  onSelect?: (item: T) => void;
  onClose: () => void;
}): KeyboardNavStore {
  const highlightedIndex = signal(-1);

  return {
    highlightedIndex,

    onKeydown(
      event: KeyboardEvent,
      optionItems: readonly ElementRef<HTMLElement>[]
    ): void {
      const results = options.results();

      if (results.length === 0) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          options.onClose();
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          highlightedIndex.update(i => (i < results.length - 1 ? i + 1 : 0));
          optionItems[highlightedIndex()]?.nativeElement.scrollIntoView({
            block: 'nearest',
          });
          break;

        case 'ArrowUp':
          event.preventDefault();
          highlightedIndex.update(i => (i > 0 ? i - 1 : results.length - 1));
          optionItems[highlightedIndex()]?.nativeElement.scrollIntoView({
            block: 'nearest',
          });
          break;

        case 'Enter':
          event.preventDefault();
          if (highlightedIndex() >= 0) {
            options.onSelect?.(results[highlightedIndex()]);
          }
          break;

        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          options.onClose();
          break;
      }
    },

    reset(): void {
      highlightedIndex.set(-1);
    },
  };
}
